import { useFormikContext } from 'formik'
import { get } from 'lodash'
import { rem } from 'polished'
import React, { useCallback } from 'react'
import { useDebounce, useUnmount } from 'react-use'
import { LEAD_TIME_MAX_ORDERABLE_QTY } from 'shared-constants'
import { DATA_TEST_CLASS } from 'shared-constants/build/testIds'
import styled from 'styled-components'
import { type FlexboxProps, type LayoutProps } from 'styled-system'
import { useProductVariantQuantity } from '@public/hooks'
import {
  getProductSlug,
  getProductVariantSlug,
  getVariantsLeadTime,
} from '@public/Showroom/Products/Detail/utils'
import { Flex } from 'components/Layout'
import { LinkWithText } from 'components/Link'
import {
  ProductDetailDescription,
  ProductOptionRow,
} from 'components/ProductOptionRow'
import { Text } from 'components/Typography'
import { ROUTE } from 'constants/routes'
import { type CartContextItem } from 'providers/cart'
import { useUserAuth } from 'providers/userAuth'
import { SPACE, FONT_SIZE, COLOR_INTENT, FONT_WEIGHT } from 'Theme'
import { type CalculatePriceCheckoutQuery } from 'types/graphql-generated'
import { formatLeadTimeString, formatQuantityWithUnit } from 'utils/formatters'
import { ProductImage } from '../ProductImage'
import { ProductPrice } from '../ProductPrice'
import { ProductQuantityCounterField } from '../ProductQuantityCounterField'

const DEBOUNCE_IN_MILLISECONDS = 500
const IMAGE_SIZE = { MOBILE: rem(90), TABLET: rem(108) }
const DEFAULT_TITLE_STYLE_PROPS = {
  fontSize: FONT_SIZE.PX_16,
  fontWeight: FONT_WEIGHT.MEDIUM,
}

const LinkText = styled(Text)`
  max-width: fit-content;
  flex-shrink: 0;
  cursor: pointer;
  text-decoration: underline;
  &:hover {
    color: ${COLOR_INTENT.GRAY_70};
  }
`

const StyledLinkWithText = styled(LinkWithText)`
  &:hover {
    cursor: pointer;
    text-decoration: underline;
  }
`

interface ProductListItemProps extends FlexboxProps, LayoutProps {
  id: number
  title: string
  quantity: number
  optionValues?: CartContextItem['productVariant']['optionValues']
  quantityFieldName: string
  cartQuantity: number
  images?: {
    id: number
    url: string
    alt?: string
  }[]
  price?: CartContextItem['productVariant']['price']
  tradePrice?: CartContextItem['productVariant']['tradePrice']
  onSetCartItem: (quantityValue: number) => void
  onRemove: () => void
  isCheckout?: boolean
  isLoading?: boolean
  managedCustomFields: CartContextItem['productVariant']['managedCustomFields']
  product: CartContextItem['productVariant']['product']
  discount?: CalculatePriceCheckoutQuery['calculatePriceCheckout']['discountItems'][number]['itemUnitDiscount']
}

export const ProductListItem: React.FC<React.PWC<ProductListItemProps>> = ({
  id,
  title,
  quantity,
  quantityFieldName,
  optionValues = [],
  cartQuantity,
  images,
  price,
  tradePrice,
  onSetCartItem,
  onRemove,
  isCheckout,
  isLoading,
  managedCustomFields,
  product,
  discount,
  ...rest
}) => {
  const { title: productTitle, id: productId } = product
  const { user } = useUserAuth()
  const { minimumOrderQuantity, quantityUnit } = useProductVariantQuantity({
    managedCustomFields: {
      ...managedCustomFields,
      enableCounter: true, // counter is always enabled in cart
    },
  })
  const { values: formValues } = useFormikContext()
  const leadTime = getVariantsLeadTime({
    managedCustomFields,
  })
  const isNotEligible = Boolean(managedCustomFields?.returnNotEligible)
  const setCartItemIfIsQuantityChanged = useCallback(() => {
    const inputQuantity = get(formValues, quantityFieldName)
    if (
      inputQuantity >= minimumOrderQuantity &&
      inputQuantity !== cartQuantity
    ) {
      onSetCartItem(inputQuantity)
    }
  }, [
    formValues,
    quantityFieldName,
    minimumOrderQuantity,
    cartQuantity,
    onSetCartItem,
  ])

  useDebounce(setCartItemIfIsQuantityChanged, DEBOUNCE_IN_MILLISECONDS, [
    get(formValues, quantityFieldName),
  ])
  useUnmount(setCartItemIfIsQuantityChanged)

  const isQuantityCounterShown =
    leadTime || (quantity >= minimumOrderQuantity && quantity > 1)

  const productURL = ROUTE.SHOWROOM_PRODUCT_DETAIL({
    slug: getProductSlug({
      id: productId,
      title: productTitle,
    }),
    variantSlug: getProductVariantSlug({
      id,
      title,
    }),
  })

  return (
    <Flex {...rest}>
      <ProductImage
        src={images[0]?.url}
        alt={images[0]?.alt || title}
        width={IMAGE_SIZE}
      />
      <Flex
        width="100%"
        flexDirection="column"
        justifyContent="space-between"
        hover={{ backgroundColor: COLOR_INTENT.GRAY_10 }}
        p={{ MOBILE: SPACE.PX_15, TABLET: SPACE.PX_20 }}
      >
        <Flex
          mb={SPACE.PX_4}
          justifyContent="space-between"
          alignItems="center"
        >
          <StyledLinkWithText
            href={productURL}
            color={COLOR_INTENT.GRAY_90}
            pr={SPACE.PX_15}
            {...(!isCheckout && DEFAULT_TITLE_STYLE_PROPS)}
          >
            {title}
          </StyledLinkWithText>
          <Flex flexDirection="column" alignItems="end">
            <ProductPrice
              price={
                user?.isTradeCapable && tradePrice.total > 0
                  ? tradePrice
                  : price
              }
            />
            {discount && (
              <ProductPrice
                isDiscount
                color={COLOR_INTENT.DISCOUNT.CODE_APPLIED}
                price={discount}
              />
            )}
          </Flex>
        </Flex>
        <Flex maxWidth="80%" flexDirection="column">
          {optionValues.map((option, idx) => (
            <ProductOptionRow
              key={idx}
              name={option.productOption.name}
              value={option.value}
            />
          ))}
          {leadTime && (
            <ProductDetailDescription>
              {formatLeadTimeString(leadTime)}
            </ProductDetailDescription>
          )}
          {isNotEligible && (
            <Text
              fontSize={FONT_SIZE.PX_12}
              fontStyle="italic"
              my={SPACE.PX_12}
            >
              <Text
                fontSize={FONT_SIZE.PX_12}
                fontWeight={FONT_WEIGHT.MEDIUM}
                fontStyle="italic"
              >
                Please note:
              </Text>
              &nbsp;This item is final sale and cannot be returned
            </Text>
          )}
        </Flex>
        <Flex
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mt={SPACE.PX_4}
        >
          {isQuantityCounterShown ? (
            <ProductQuantityCounterField
              data-test-id={DATA_TEST_CLASS.CART_LIST_ITEM_COUNTER}
              data-test-class={DATA_TEST_CLASS.CART_LIST_ITEM_COUNTER}
              id={id.toString()}
              name={quantityFieldName}
              minimumOrderQuantity={minimumOrderQuantity}
              availableQuantity={
                leadTime ? LEAD_TIME_MAX_ORDERABLE_QTY : quantity
              }
              quantityUnit={quantityUnit}
              isDisabled={isLoading}
            />
          ) : (
            <Text data-test-class={DATA_TEST_CLASS.CART_LIST_ITEM_QUANTITY_ONE}>
              {`Qty ${formatQuantityWithUnit({
                quantity: cartQuantity,
                unit: quantityUnit,
                isUnitInParentheses: true,
              })}`}
            </Text>
          )}
          <LinkText
            as="button"
            mt={isQuantityCounterShown ? SPACE.PX_32 : 0}
            textAlign="right"
            disabled={isLoading}
            data-test-class={DATA_TEST_CLASS.CART_REMOVE_ITEM_BUTTON}
            fontSize={FONT_SIZE.PX_12}
            color={COLOR_INTENT.GRAY_50}
            onClick={onRemove}
          >
            Remove
          </LinkText>
        </Flex>
      </Flex>
    </Flex>
  )
}
